import React, { useEffect, useState } from "react";
import { Sun, Moon, Monitor, Plus, User, Shield, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Ayarlar() {
  const { user, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode: "create" | "edit", user }
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "site_chief", permissions: [] });
  const [customRole, setCustomRole] = useState(false);

  const load = async () => {
    if (user?.role !== "admin") return;
    const [{ data: u }, { data: c }] = await Promise.all([
      api.get("/auth/users"),
      api.get("/auth/permissions"),
    ]);
    setUsers(u);
    setCatalog(c);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const themeOptions = [
    { key: "light", label: "Aydınlık", icon: Sun },
    { key: "dark", label: "Karanlık", icon: Moon },
    { key: "system", label: "Sistem", icon: Monitor },
  ];

  const openCreate = () => {
    setForm({
      email: "", name: "", password: "", role: "site_chief",
      permissions: catalog?.site_chief_defaults || [],
    });
    setCustomRole(false);
    setDialog({ mode: "create" });
  };

  const openEdit = (u) => {
    setForm({
      email: u.email, name: u.name, password: "", role: u.role, permissions: u.permissions || [],
    });
    setCustomRole(!["admin", "site_chief"].includes(u.role));
    setDialog({ mode: "edit", user: u });
  };

  const changeRole = (role) => {
    if (role === "__custom__") {
      setCustomRole(true);
      setForm((f) => ({ ...f, role: "" }));
      return;
    }
    setCustomRole(false);
    setForm((f) => ({
      ...f,
      role,
      permissions:
        role === "admin"
          ? (catalog?.all || [])
          : role === "site_chief"
          ? (catalog?.site_chief_defaults || [])
          : f.permissions,
    }));
  };

  const togglePerm = (perm) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter((p) => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const submit = async () => {
    try {
      if (dialog.mode === "create") {
        await api.post("/auth/register", form);
        toast.success("Kullanıcı oluşturuldu");
      } else {
        const payload = { name: form.name, role: form.role, permissions: form.permissions };
        if (form.email && form.email !== dialog.user.email) payload.email = form.email;
        if (form.password) payload.password = form.password;
        await api.patch(`/auth/users/${dialog.user.id}`, payload);
        toast.success("Kullanıcı güncellendi");
        if (dialog.user.id === user.id) refresh();
      }
      setDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.name} kullanıcısını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="ayarlar-page">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Sistem</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Ayarlar</h1>
      </div>

      <Tabs defaultValue="theme">
        <TabsList>
          <TabsTrigger value="theme" data-testid="tab-theme">Görünüm</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger value="users" data-testid="tab-users">Kullanıcılar</TabsTrigger>}
        </TabsList>

        <TabsContent value="theme" className="mt-6">
          <Card className="p-6 shadow-none rounded-md max-w-xl">
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-4">Tema</div>
            <div className="grid grid-cols-3 gap-3">
              {themeOptions.map((opt) => {
                const Icon = opt.icon;
                const active = theme === opt.key;
                return (
                  <button
                    key={opt.key}
                    data-testid={`theme-${opt.key}`}
                    onClick={() => setTheme(opt.key)}
                    className={`p-4 rounded-md border transition-all ${active ? "border-brand bg-brand/5" : "border-border hover:border-brand/50"}`}
                  >
                    <Icon className={`w-5 h-5 mb-2 ${active ? "text-brand" : "text-muted-foreground"}`} />
                    <div className={`text-sm font-medium ${active ? "text-brand" : ""}`}>{opt.label}</div>
                  </button>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {user?.role === "admin" && (
          <TabsContent value="users" className="mt-6 space-y-4">
            <div className="flex justify-end">
              <Button data-testid="new-user-btn" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" /> Kullanıcı Ekle
              </Button>
            </div>
            <div className="space-y-3">
              {users.map((u) => (
                <Card key={u.id} className="p-4 shadow-none rounded-md flex items-center gap-4" data-testid={`user-card-${u.id}`}>
                  <div className="w-10 h-10 rounded-full bg-secondary border border-border flex items-center justify-center font-semibold">
                    {u.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{u.name}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${u.role === "admin" ? "bg-brand/15 text-brand" : "bg-secondary text-muted-foreground"}`}>
                        {u.role === "admin" ? "Yönetici" : u.role === "site_chief" ? "Şantiye Şefi" : u.role}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">{u.permissions?.length || 0} yetki</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                      <Shield className="w-3 h-3 mr-1" /> Düzenle
                    </Button>
                    {u.id !== user.id && (
                      <Button size="sm" variant="ghost" onClick={() => remove(u)} className="text-destructive">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {dialog?.mode === "create" ? "Yeni Kullanıcı" : "Kullanıcı Düzenle"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Ad Soyad</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>E-posta</Label>
                <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Rol</Label>
                <Select value={customRole ? "__custom__" : form.role} onValueChange={changeRole}>
                  <SelectTrigger data-testid="user-role"><SelectValue placeholder="Rol seç" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Yönetici</SelectItem>
                    <SelectItem value="site_chief">Şantiye Şefi</SelectItem>
                    <SelectItem value="__custom__">+ Özel Rol…</SelectItem>
                  </SelectContent>
                </Select>
                {customRole && (
                  <Input
                    data-testid="user-role-custom"
                    className="mt-2"
                    placeholder="Özel rol adı (örn: muhasebeci)"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                  />
                )}
              </div>
              <div className="space-y-2"><Label>{dialog?.mode === "edit" ? "Yeni Şifre (opsiyonel)" : "Şifre"}</Label>
                <Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">Yetki Matrisi</div>
              {catalog && (
                <div className="border border-border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">Modül</th>
                        {catalog.actions.map((a) => (
                          <th key={a.key} className="text-center px-3 py-2 font-semibold text-xs uppercase tracking-wider">{a.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {catalog.modules.map((m) => (
                        <tr key={m.key} className="border-t border-border">
                          <td className="px-3 py-2 font-medium">{m.label}</td>
                          {catalog.actions.map((a) => {
                            const p = `${m.key}.${a.key}`;
                            return (
                              <td key={a.key} className="text-center px-3 py-2">
                                <Checkbox
                                  data-testid={`perm-${p}`}
                                  checked={form.permissions.includes(p)}
                                  onCheckedChange={() => togglePerm(p)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-2">
                Seçili: {form.permissions.length} / {catalog?.all?.length || 32} yetki
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>İptal</Button>
            <Button data-testid="user-save" onClick={submit} disabled={!form.name || !form.role || (dialog?.mode === "create" && (!form.email || !form.password))}>
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
